/**
 * Per-contact "Form/Phone upload" button for direct-upload (google_ads)
 * locations.
 *
 * Sibling to TestUploadButton. Where that button fires a fixed qualified/
 * converted event, this one lets the qualifier detect — from the contact's GHL
 * tags — whether the contact is a form-submit (`new survey lead`) or phone-call
 * (`new phone lead`) lead, then uploads the matching conversion. The caller
 * sends only the contact + location; the resolved event type comes back in the
 * result and is surfaced in the toast. Shown only when the location is in
 * google_ads mode AND the per-location `test_button_enabled` toggle is on (the
 * same gate as TestUploadButton). The server endpoint enforces it too.
 */

import { Loader2, PhoneCall } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'
import { useTestUploadFormCallConversion } from '@/hooks/useGHLQualifierDashboard'

interface Props {
  readonly contactId: string
  readonly locationId: string
}

export function FormCallUploadButton({ contactId, locationId }: Props) {
  const { toast } = useToast()
  const mutation = useTestUploadFormCallConversion()

  const fire = () => {
    mutation.mutate(
      { contactId, locationId },
      {
        onSuccess: (res) => {
          if (!res.ok) {
            toast({
              title: 'Upload skipped',
              description: res.message ?? res.reason ?? 'Nothing to upload for this contact.',
            })
            return
          }
          toast({
            title: `${res.eventType} upload: ${res.status}${res.validateOnly ? ' (validateOnly)' : ''}`,
            description: res.error ? `Error: ${res.error}` : `transaction ${res.transactionId}`,
            variant: res.status === 'failed' ? 'destructive' : 'default',
          })
        },
        onError: (err: unknown) => {
          toast({
            title: 'Form/Phone upload failed',
            description: err instanceof Error ? err.message : 'Unknown error',
            variant: 'destructive',
          })
        },
      }
    )
  }

  return (
    <Button
      size="sm"
      variant="outline"
      className="h-7 gap-1.5"
      disabled={mutation.isPending}
      onClick={(e) => {
        e.stopPropagation() // don't trigger row navigation
        fire()
      }}
    >
      {mutation.isPending ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <PhoneCall className="h-3.5 w-3.5" />
      )}
      Form/Phone
    </Button>
  )
}
